/*
  GEMDRAW — Sticky shell, anchor navigation and active section

  The announcement strip and the header travel together as one pinned shell.
  The pinning itself is CSS: this file only measures the two parts and
  publishes their heights, so the anchor offset and the mobile drawer height
  stay correct when the marquee is edited, disabled or wraps.

  It also owns the two navigation behaviors the header needs on a single-page
  storefront: an anchor offset that clears the shell, and a restrained active
  state driven by which section is currently in view.
*/

(function () {
  'use strict';

  const ROOT = document.documentElement;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Shell measurement -------------------------------------------------------- */

  function measure() {
    const marquee = document.querySelector('[data-gd-marquee]');
    const header = document.querySelector('.section-header');

    const marqueeH = marquee ? Math.round(marquee.getBoundingClientRect().height) : 0;
    const headerH = header ? Math.round(header.getBoundingClientRect().height) : 0;

    ROOT.style.setProperty('--gd-marquee-h', marqueeH + 'px');
    ROOT.style.setProperty('--gd-shell-h', marqueeH + headerH + 'px');
  }

  let measureFrame = null;
  function scheduleMeasure() {
    if (measureFrame !== null) return;
    measureFrame = window.requestAnimationFrame(function () {
      measureFrame = null;
      measure();
    });
  }

  function watchShell() {
    measure();

    /* The marquee's height depends on the loaded webfont, so a first pass
       before the swap would publish the fallback face's height. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleMeasure);
    }

    window.addEventListener('resize', scheduleMeasure, { passive: true });

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(scheduleMeasure);
      const marquee = document.querySelector('[data-gd-marquee]');
      const header = document.querySelector('.section-header');
      if (marquee) ro.observe(marquee);
      if (header) ro.observe(header);
    }
  }

  /* Anchor navigation -------------------------------------------------------- */

  /*
    Same-page anchors are handled here rather than left to the browser, because
    the browser scrolls the target to the very top of the viewport where the
    pinned shell would cover it. scroll-margin-top handles that for most
    engines, but focus management still has to be explicit: the target is made
    programmatically focusable for one interaction so a keyboard user actually
    lands inside the section they asked for.
  */
  function isSamePageAnchor(link) {
    if (!link || !link.hash || link.hash.length < 2) return false;
    return (
      link.pathname === window.location.pathname &&
      link.search === window.location.search &&
      link.host === window.location.host
    );
  }

  function focusTarget(target) {
    if (target.hasAttribute('tabindex')) {
      target.focus({ preventScroll: true });
      return;
    }

    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    target.addEventListener(
      'blur',
      function () {
        target.removeAttribute('tabindex');
      },
      { once: true }
    );
  }

  function onAnchorClick(event) {
    const link = event.target.closest('a[href*="#"]');
    if (!link || !isSamePageAnchor(link)) return;

    let target;
    try {
      target = document.querySelector(link.hash);
    } catch (error) {
      return;
    }
    if (!target) return;

    event.preventDefault();

    target.scrollIntoView({
      behavior: reduceQuery.matches ? 'auto' : 'smooth',
      block: 'start',
    });

    /* Keyboard users are moved into the section; pointer users are not, so a
       click does not steal focus into the page body. */
    if (document.body.classList.contains('gd-using-keyboard')) focusTarget(target);

    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', link.hash);
    }
  }

  /* Only mark keyboard use, so focus is moved for the people who need it. */
  function watchInputMode() {
    window.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'Tab') document.body.classList.add('gd-using-keyboard');
      },
      { passive: true }
    );

    window.addEventListener(
      'pointerdown',
      function () {
        document.body.classList.remove('gd-using-keyboard');
      },
      { passive: true }
    );
  }

  /* Active section ----------------------------------------------------------- */

  /*
    Marks the navigation item matching whichever anchored section currently owns
    the top of the reading area. The state is carried by an underline and a
    weight change in CSS, never by color alone, and aria-current makes it
    available to assistive technology.
  */
  function watchActiveSection() {
    const links = Array.prototype.filter.call(
      document.querySelectorAll('[data-gd-nav-anchor]'),
      function (link) {
        return link.hash && document.querySelector(link.hash);
      }
    );

    if (!links.length || !('IntersectionObserver' in window)) return;

    const byId = {};
    links.forEach(function (link) {
      byId[link.hash.slice(1)] = link;
    });

    const visible = new Set();

    function paint() {
      let current = null;
      /* The section highest on the page wins, so scrolling never flickers
         between two sections that are both partly in view. */
      links.forEach(function (link) {
        const id = link.hash.slice(1);
        if (visible.has(id) && current === null) current = id;
      });

      links.forEach(function (link) {
        const active = link.hash.slice(1) === current;
        link.classList.toggle('gd-nav-item--active', active);
        if (active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }

    const io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        paint();
      },
      /* The band sits just under the shell, so a section counts as current
         while it occupies the top of what is actually readable. */
      { rootMargin: '-30% 0px -55% 0px', threshold: 0 }
    );

    Object.keys(byId).forEach(function (id) {
      const section = document.getElementById(id);
      if (section) io.observe(section);
    });
  }

  /* Wiring ------------------------------------------------------------------- */

  function start() {
    watchShell();
    watchInputMode();
    watchActiveSection();
    document.addEventListener('click', onAnchorClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* Editing the marquee or the header re-renders it at a new height. */
  document.addEventListener('shopify:section:load', scheduleMeasure);
  document.addEventListener('shopify:section:unload', scheduleMeasure);
})();
